import { DateTime as DT } from 'luxon';
import { TaskTypeStatus, type TaskType } from '@econtractor/types';
import {
    isoDateToEndMillis,
    isoDateToStartMillis,
    millisToIsoDate,
    millisToTime,
    resolveDateEndMillis,
    resolveDateStartMillis,
} from '../../NewTask/helpers/initNewTaskStruct';
import { USERS_HISTORY_MARKER } from './taskUsersHelpers';

/** Службовий маркер у reject_comment для подій зміни терміну */
export const TERM_HISTORY_MARKER = '__TERM__';
export const REMIND_HISTORY_MARKER = '__REMIND__';

export interface TaskDueDateValues {
    multiDay: boolean;
    withTime: boolean;
    dateStart: number;
    dateEnd: number;
    startTime: string;
    endTime: string;
}

/** Контролер може змінювати термін лише для NEW / INWORK (не видалених) */
export const canEditTaskTerm = (task: TaskType, userId: number): boolean => {
    if (!(userId > 0)) return false;
    if (Boolean(Number(task.taskDeleted))) return false;
    if (userId !== Number(task.taskController)) return false;
    const status = Number(task.taskStatus);
    return status === TaskTypeStatus.NEW || status === TaskTypeStatus.INWORK;
};

export const dueDatesFromTask = (task: TaskType): TaskDueDateValues => {
    const withTime = Boolean(task.withTime);
    const endTimeRaw = String(task.endTime || '').trim();
    const endTime = endTimeRaw
        ? endTimeRaw.slice(0, 5)
        : millisToTime(task.dateEnd);
    const dateStart = Number(task.dateStart) || DT.now().setZone('local').startOf('day').toMillis();

    return {
        multiDay: Boolean(task.multiDay),
        withTime,
        dateStart,
        dateEnd: Number(task.dateEnd) || DT.now().setZone('local').endOf('day').toMillis(),
        startTime: withTime ? millisToTime(dateStart, '00:00') : '00:00',
        endTime: endTime || '18:00',
    };
};

export const normalizeDueDateValues = (values: TaskDueDateValues) => {
    const dateEnd = resolveDateEndMillis(values.dateEnd, values.withTime, values.endTime);
    const dateStart = resolveDateStartMillis(
        values.dateStart,
        dateEnd,
        values.multiDay,
        values.withTime,
        values.startTime,
    );

    return {
        multiDay: values.multiDay,
        withTime: values.withTime,
        dateStart,
        dateEnd,
        endTime: values.withTime ? String(values.endTime || '18:00').trim() || null : null,
    };
};

/** Як у таблиці: start/end з часом якщо withTime */
const formatTermPoint = (millis: number, withTime: boolean): string => {
    const dt = DT.fromMillis(millis, { zone: 'local' });
    if (!dt.isValid) return '—';
    return withTime ? dt.toFormat('dd.MM.yyyy HH:mm') : dt.toFormat('dd.MM.yyyy');
};

/** Коментар історії: `15.07.2026 00:00 → 20.07.2026 18:00` (в один рядок) */
export const buildTermHistoryComment = (
    values: ReturnType<typeof normalizeDueDateValues>,
): string => {
    const startLabel = formatTermPoint(values.dateStart, values.withTime);
    const endLabel = formatTermPoint(values.dateEnd, values.withTime);
    return `${TERM_HISTORY_MARKER}\n${startLabel} → ${endLabel}`;
};

export const parseHistoryComment = (
    raw: string | null | undefined,
): { kind: 'term' | 'users' | 'remind' | 'plain'; text: string } => {
    const value = String(raw || '');
    const termPrefix = `${TERM_HISTORY_MARKER}\n`;
    if (value.startsWith(termPrefix)) {
        return { kind: 'term', text: value.slice(termPrefix.length) };
    }
    if (value.startsWith(TERM_HISTORY_MARKER)) {
        return { kind: 'term', text: value.slice(TERM_HISTORY_MARKER.length).replace(/^\n/, '') };
    }

    const usersPrefix = `${USERS_HISTORY_MARKER}\n`;
    if (value.startsWith(usersPrefix)) {
        return { kind: 'users', text: value.slice(usersPrefix.length) };
    }
    if (value.startsWith(USERS_HISTORY_MARKER)) {
        return { kind: 'users', text: value.slice(USERS_HISTORY_MARKER.length).replace(/^\n/, '') };
    }

    const remindPrefix = `${REMIND_HISTORY_MARKER}\n`;
    if (value.startsWith(remindPrefix)) {
        return { kind: 'remind', text: value.slice(remindPrefix.length) };
    }
    if (value.startsWith(REMIND_HISTORY_MARKER)) {
        return { kind: 'remind', text: value.slice(REMIND_HISTORY_MARKER.length).replace(/^\n/, '') };
    }

    return { kind: 'plain', text: value };
};

export const applyEndDateChange = (
    values: TaskDueDateValues,
    isoDate: string,
): Partial<TaskDueDateValues> => {
    const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
    if (endMillis == null) return {};
    if (!values.multiDay) {
        return {
            dateEnd: endMillis,
            dateStart: isoDateToStartMillis(isoDate, values.withTime, values.startTime)
                ?? values.dateStart,
        };
    }
    return { dateEnd: endMillis };
};

export const applyStartDateChange = (
    values: TaskDueDateValues,
    isoDate: string,
): Partial<TaskDueDateValues> => {
    const startMillis = isoDateToStartMillis(isoDate, values.withTime, values.startTime);
    if (startMillis == null) return {};

    const next: Partial<TaskDueDateValues> = { dateStart: startMillis };
    const endStartOfDay = isoDateToStartMillis(millisToIsoDate(values.dateEnd));
    if (endStartOfDay != null && startMillis > endStartOfDay) {
        const endMillis = isoDateToEndMillis(isoDate, values.withTime, values.endTime);
        if (endMillis != null) next.dateEnd = endMillis;
    }
    return next;
};

export const applyWithTimeChange = (
    values: TaskDueDateValues,
    checked: boolean,
): Partial<TaskDueDateValues> => {
    const startTime = '00:00';
    const endIso = millisToIsoDate(values.dateEnd);
    const startIso = values.multiDay ? millisToIsoDate(values.dateStart) : endIso;
    const endMillis = isoDateToEndMillis(endIso, checked, values.endTime);
    const startMillis = isoDateToStartMillis(startIso, checked, startTime);
    return {
        withTime: checked,
        startTime,
        ...(startMillis != null ? { dateStart: startMillis } : {}),
        ...(endMillis != null ? { dateEnd: endMillis } : {}),
    };
};

export const applyStartTimeChange = (
    values: TaskDueDateValues,
    time: string,
): Partial<TaskDueDateValues> => {
    const startIso = values.multiDay
        ? millisToIsoDate(values.dateStart)
        : millisToIsoDate(values.dateEnd);
    const startMillis = isoDateToStartMillis(startIso, true, time);
    return {
        startTime: time,
        ...(startMillis != null ? { dateStart: startMillis } : {}),
    };
};

export const applyEndTimeChange = (
    values: TaskDueDateValues,
    time: string,
): Partial<TaskDueDateValues> => {
    const endIso = millisToIsoDate(values.dateEnd);
    const endMillis = isoDateToEndMillis(endIso, true, time);
    return {
        endTime: time,
        ...(endMillis != null ? { dateEnd: endMillis } : {}),
    };
};

export const applyMultiDayChange = (
    values: TaskDueDateValues,
    checked: boolean,
): Partial<TaskDueDateValues> => {
    if (!checked) {
        return {
            multiDay: false,
            dateStart: isoDateToStartMillis(
                millisToIsoDate(values.dateEnd),
                values.withTime,
                values.startTime,
            ) ?? values.dateStart,
        };
    }
    return { multiDay: true };
};
